import { ScrollingModule } from '@angular/cdk/scrolling'
import { CommonModule } from '@angular/common'
import { Component, ComponentRef, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild, ViewContainerRef } from '@angular/core'
import { UnsavedChangesModalComponent } from '@app/v2/shared/components/unsaved-changes-modal/unsaved-changes-modal.component'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { take } from 'rxjs/operators'
import { WizardHeaderComponent } from './components/wizard-header/wizard-header.component'
import { WizardService } from './services/wizard.service'

@Component({
  selector: 'wizard',
  standalone: true,
  imports: [CommonModule, WizardHeaderComponent, UnsavedChangesModalComponent, ScrollingModule],
  templateUrl: './wizard.component.html',
  styleUrl: './wizard.component.scss'
})
export class WizardComponent {
  @Input() public breadcrumbs: string[] = []
  @Input() public contentRef: ComponentRef<any> | null = null
  @ViewChild('contentHost', { read: ViewContainerRef, static: true }) public contentHost!: ViewContainerRef
  @ViewChild('wizardHeader', { read: ElementRef }) public wizardHeader!: ElementRef<HTMLElement>
  @Output() public onClose = new EventEmitter<void>()
  protected readonly UNSAVED_MODAL_ID = 'wizard-unsaved-changes-modal'

  constructor(private _modalService: ModalService, private _wizardService: WizardService) {}

  @HostListener('document:keydown.escape')
  public handleEscape(): void {
    if (!this._wizardService.isTopOverlayWizard(this)) return
    // If any modal dialog is open, let the modal handle Escape
    if (this._modalService.hasOpenModal()) return
    // A Nebular/CDK overlay is on top of this wizard — do not close both in the same press.
    if (document.querySelector('.cdk-overlay-backdrop.cdk-overlay-backdrop-showing')) return
    // If a body-mounted dropdown overlay is open, let it handle Escape first
    if (document.querySelector('app-dropdown-content')) return
    this.close()
  }

  /**
   * @description Close the wizard when clicking the container (backdrop) but not the header or body.
   * @param event - The mouse event
   * @param hostElement - The host element
   * @param wizardHeader - The wizard header element
   */
  public onContainerClick(event: MouseEvent, hostElement: HTMLElement): void {
    if (!this.wizardHeader?.nativeElement?.contains(event.target as Node) && !hostElement?.contains(event.target as Node)) {
      this.close()
    }
  }

  /**
   * Request to close the wizard. If the content has unsaved changes, cancel any pending save and show a warning modal instead.
   */
  public close(): void {
    if (this.contentRef?.instance?.blockWizardClose?.()) return
    const hasUnsaved = this.contentRef?.instance?.hasUnsavedChanges?.()
    if (hasUnsaved) {
      this.contentRef?.instance?.cancelPendingBlurSave?.()
      this._modalService.toggleModal(this.UNSAVED_MODAL_ID)
      return
    }
    this.onClose.emit()
  }

  /**
   * User chose "Save changes" — save unsaved fields (modal closes itself).
   */
  public saveChangesAndStay(): void {
    this.contentRef?.instance?.saveUnsavedChanges?.()
  }

  /**
   * User chose "Cancel" — leave the wizard (modal closes itself).
   */
  public cancelAndLeave(): void {
    this.onClose.emit()
  }

  /** User chose "Save changes": save, then close wizard. Stay open if save/validation fails. */
  public saveChangesAndLeave(): void {
    const saveResult = this.contentRef?.instance?.saveUnsavedChanges?.()
    if (!saveResult) {
      this.onClose.emit()
      return
    }
    saveResult.pipe(take(1)).subscribe({
      next: () => this.onClose.emit(),
      error: () => undefined
    })
  }

  /** User chose "Discard changes": revert form, close modal, then close wizard. */
  public discardAndLeave(): void {
    this.contentRef?.instance?.revertToSaved?.()
    if (this._modalService.isModalOpen(this.UNSAVED_MODAL_ID)) this._modalService.toggleModal(this.UNSAVED_MODAL_ID)
    this.onClose.emit()
  }

  /** User closed with X/Escape: close modal only, keep unsaved edits. */
  public closeModalOnly(): void {
    this.contentRef?.instance?.cancelPendingBlurSave?.()
    if (this._modalService.isModalOpen(this.UNSAVED_MODAL_ID)) this._modalService.toggleModal(this.UNSAVED_MODAL_ID)
  }
}
